/**
 * ADR-27 lot kinds - public surface.
 *
 * Usage at every write boundary (API, CMS, importers):
 *
 *   const result = validateLotAttributes(lot.kind, payload.attributes);
 *   if (!result.ok) reject(result.issues);
 *
 * and at release time:
 *
 *   const missing = missingReleaseDocs(lot.kind, lot.attributes, docsOnFile);
 */

import { zodToJsonSchema } from 'zod-to-json-schema';
import type { DocRequirement, LotKindDefinition, ValidationResult } from './registry.js';
import { equine } from './kinds/equine.js';
import { cattle, exotic } from './kinds/livestock.js';
import { general, notice, tack, trailer } from './kinds/equipment.js';

export type { DetailDef, DocRequirement, FacetDef, LotKindDefinition, ValidationResult } from './registry.js';

// The equine subtype vocabulary (ADR-66 PR 2): one canonical definition.
export {
  EQUINE_TYPES, SALE_SELECTABLE_EQUINE_TYPES, DEFAULT_ACCEPTED_EQUINE_TYPES,
  isSaleSelectableEquineType, parseAcceptedEquineTypes, equineTypeSchema, saleSelectableEquineTypeSchema,
} from './equine-types.js';
export type { EquineType, SaleSelectableEquineType } from './equine-types.js';

// ADR-46 conversational intake: per-kind field roles, aliases, media
// requirements, and the generic completeness engine for the flat kinds.
export {
  KIND_INTAKE,
  RESERVE_KEY,
  getKindIntake,
  matchItemTypeAliases,
  itemIntakeStatus,
  factsToAttributes,
} from './intake.js';
export type {
  FieldRole,
  IntakeFieldDef,
  MediaRequirement,
  KindIntakeDefinition,
  KindMatch,
  IntakeFieldMiss,
  IntakeDocStatus,
  IntakeMediaState,
  ItemIntakeStatus,
} from './intake.js';

export const KINDS: ReadonlyMap<string, LotKindDefinition> = new Map(
  [equine, cattle, exotic, tack, trailer, notice, general].map((k) => [k.kind, k]),
);

export const KIND_KEYS: readonly string[] = [...KINDS.keys()];

/**
 * Transitional compatibility for the horse -> equine kind rename (ADR-66).
 * Existing rows are migrated by the forward migration, but the append-only,
 * hash-chained evidence ledger keeps its historical `kind:'horse'` payloads
 * forever, and a rolling deploy can briefly overlap old code. Normalize a legacy
 * `'horse'` kind value to `'equine'` at read boundaries. Remove in the ADR-66
 * cleanup cut once prod is confirmed free of legacy `'horse'` rows.
 */
export function normalizeKind(kind: string): string {
  return kind === 'horse' ? 'equine' : kind;
}

export function getKind(kind: string): LotKindDefinition {
  const def = KINDS.get(normalizeKind(kind));
  if (!def) throw new Error(`unknown lot kind: ${kind}`);
  return def;
}

/** Validate a JSONB attributes payload against its kind's schema. Strict -
 *  unknown keys are write-time errors, not silent dead data. */
export function validateLotAttributes(kind: string, attributes: unknown): ValidationResult {
  const def = KINDS.get(normalizeKind(kind));
  if (!def) return { ok: false, issues: [{ path: 'kind', message: `unknown lot kind: ${kind}` }] };
  const parsed = def.attributes.safeParse(attributes ?? {});
  if (parsed.success) return { ok: true, attributes: parsed.data as Record<string, unknown> };
  return {
    ok: false,
    issues: parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
  };
}

/**
 * The kind's attribute schema as JSON Schema - the SAME Zod schema
 * validateLotAttributes enforces, so a consumer (the MCP lot-kinds resource,
 * an AI agent, OpenAPI) sees required sub-keys, arrays, enums and strictness
 * up front instead of learning them from 422s.
 */
export function attributeJsonSchema(kind: string): Record<string, unknown> {
  const def = getKind(kind);
  return zodToJsonSchema(def.attributes, { $refStrategy: 'none', target: 'jsonSchema7' }) as Record<string, unknown>;
}

/** The document types this specific lot must have on file before release,
 *  resolved against its attributes. */
export function requiredReleaseDocs(
  kind: string,
  attributes: Record<string, unknown>,
): readonly DocRequirement[] {
  return getKind(kind).releaseChecklist.filter(
    (d) => d.required || (d.requiredWhen?.(attributes) ?? false),
  );
}

/** What still blocks release: required doc types with nothing on file. */
export function missingReleaseDocs(
  kind: string,
  attributes: Record<string, unknown>,
  docTypesOnFile: readonly string[],
): readonly DocRequirement[] {
  const have = new Set(docTypesOnFile);
  return requiredReleaseDocs(kind, attributes).filter((d) => !have.has(d.docType));
}
